import { useMemo } from "react";
import { useDecisionMetrics, VehicleDecisionData } from "./useDecisionMetrics";
import { useMaintenance, MaintenanceTask } from "./useMaintenance";
import { useFleetAlerts } from "./useFleetAlerts";

export type StabilityLevel = "Low" | "Medium" | "High";
export type ImpactLevel = "low" | "medium" | "high";

export interface VehicleOverview {
  vehicleId: string;
  vehicleName: string;
  attentionScore: number;
  primaryReason: string;
  impact: ImpactLevel;
  needsAttention: boolean;
  metrics: {
    monthlyProfit: number;
    utilization: number;
    expenseRatio: number;
    recentExpenseChange: number;
    tripCount: number;
  };
  tasks: {
    open: number;
    overdue: number;
    upcoming: number;
  };
  alertCount: number;
  maintenanceCount: number;
  updatedAt: string;
}

export interface FleetSummary {
  monthlyProfit: number;
  vehiclesNeedingAttention: number;
  activeAlerts: number;
  stability: StabilityLevel;
}

function vehicleName(v: VehicleDecisionData): string {
  return v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
}

function computeAttentionScore(
  v: VehicleDecisionData,
  fleetAvg: { monthlyProfit: number; expenseRatio: number; utilization: number },
  alertCount: number,
  maintenanceFreq: number
): number {
  let score = 0;

  // Profit trend decline (0-30)
  if (v.monthlyProfit < 0) score += 30;
  else if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.5) score += 20;
  else if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.7) score += 10;

  // Expense spike (0-25)
  if (v.recentExpenseChange > 60) score += 25;
  else if (v.recentExpenseChange > 40) score += 18;
  else if (v.recentExpenseChange > 25) score += 10;

  // Volatility / high expense ratio (0-15)
  if (v.expenseRatio > 85) score += 15;
  else if (v.expenseRatio > 70) score += 8;

  // Active alerts (0-15)
  score += Math.min(15, alertCount * 5);

  // Maintenance frequency (0-10)
  if (maintenanceFreq >= 4) score += 10;
  else if (maintenanceFreq >= 2) score += 5;

  // Low utilization / revenue drop (0-15)
  if (v.utilization < 15) score += 15;
  else if (v.utilization < fleetAvg.utilization * 0.5) score += 8;

  return Math.min(100, score);
}

function getPrimaryReason(v: VehicleDecisionData, fleetAvg: { monthlyProfit: number; utilization: number }): string {
  if (v.monthlyProfit < 0) return "Operating at a loss";
  if (v.recentExpenseChange > 40) return "Recent expense spike";
  if (v.expenseRatio > 80) return "High expense ratio";
  if (v.utilization < 15) return "Very low utilization";
  if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.5) return "Profit well below fleet average";
  if (v.utilization < fleetAvg.utilization * 0.5) return "Utilization below fleet average";
  if (v.recentExpenseChange > 25) return "Expenses trending up";
  return "Metrics within normal range";
}

function getImpact(score: number): ImpactLevel {
  if (score >= 50) return "high";
  if (score >= 25) return "medium";
  return "low";
}

export const useFleetOverview = () => {
  const { vehicleData, fleetAvg, isLoading: decisionLoading } = useDecisionMetrics();
  const { tasks: maintenanceTasks, isLoading: maintLoading } = useMaintenance();
  const { alerts, isLoading: alertsLoading } = useFleetAlerts();

  const isLoading = decisionLoading || maintLoading || alertsLoading;

  const vehicles = useMemo<VehicleOverview[]>(() => {
    if (!vehicleData.length) return [];

    const now = new Date();

    return vehicleData.map((v) => {
      const name = vehicleName(v);
      const vTasks = (maintenanceTasks || []).filter((t) => t.vehicle_id === v.vehicle.id);
      const openTasks = vTasks.filter((t) => !t.is_completed);
      const overdueTasks = openTasks.filter((t) => t.due_date && new Date(t.due_date) < now);
      const upcomingTasks = openTasks.filter(
        (t) => t.due_date && new Date(t.due_date) >= now && new Date(t.due_date) <= new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000)
      );

      const vehicleAlerts = alerts.filter((a) => a.vehicleId === v.vehicle.id);
      const alertCount = vehicleAlerts.length;

      const attentionScore = computeAttentionScore(v, fleetAvg, alertCount, v.maintenanceCount);

      return {
        vehicleId: v.vehicle.id,
        vehicleName: name,
        attentionScore,
        primaryReason: getPrimaryReason(v, fleetAvg),
        impact: getImpact(attentionScore),
        needsAttention: attentionScore >= 25,
        metrics: {
          monthlyProfit: v.monthlyProfit,
          utilization: v.utilization,
          expenseRatio: v.expenseRatio,
          recentExpenseChange: v.recentExpenseChange,
          tripCount: v.tripCount,
        },
        tasks: {
          open: openTasks.length,
          overdue: overdueTasks.length,
          upcoming: upcomingTasks.length,
        },
        alertCount,
        maintenanceCount: v.maintenanceCount,
        updatedAt: new Date().toISOString(),
      };
    }).sort((a, b) => b.attentionScore - a.attentionScore);
  }, [vehicleData, fleetAvg, maintenanceTasks, alerts]);

  const summary = useMemo<FleetSummary>(() => {
    const monthlyProfit = vehicleData.reduce((s, v) => s + v.monthlyProfit, 0);
    const needingAttention = vehicles.filter((v) => v.needsAttention).length;
    const activeAlerts = alerts.length;

    let stability: StabilityLevel = "High";
    if (needingAttention > vehicles.length * 0.5 || activeAlerts > vehicles.length * 2) {
      stability = "Low";
    } else if (needingAttention > vehicles.length * 0.25 || activeAlerts > vehicles.length) {
      stability = "Medium";
    }

    return { monthlyProfit, vehiclesNeedingAttention: needingAttention, activeAlerts, stability };
  }, [vehicleData, vehicles, alerts]);

  const attentionVehicles = useMemo(() => vehicles.filter((v) => v.needsAttention), [vehicles]);
  const stableVehicles = useMemo(() => vehicles.filter((v) => !v.needsAttention), [vehicles]);

  return { vehicles, attentionVehicles, stableVehicles, summary, isLoading };
};
